// M1: AI 模型配置生成器模板
//
// 5 个预设让用户在 UI 里选 + 填表单 -> 生成 agent-host.toml snippet
// 不读现有 toml, 只负责"加一个 adapter" 生成片段, user 自己粘贴到完整 config

export type FieldType = "text" | "password" | "select" | "path";

export interface TemplateField {
  key: string;
  label: string;
  type: FieldType;
  required: boolean;
  default?: string;
  placeholder?: string;
  options?: string[];
  help?: string;
  /** 字段在生成 toml 时的转义方式 */
  secret?: boolean;
}

export interface AdapterTemplate {
  id: string;
  label: string;
  description: string;
  /** 推荐的 trust_scope */
  defaultTrustScope: "auto" | "confirm-each" | "confirm-once" | "deny";
  fields: TemplateField[];
  /** 模板专属的"前置安装"提示 (展示给 user 看) */
  preInstall?: string[];
  /** 根据表单值生成 toml 片段 */
  toToml(values: Record<string, string>): string;
}

const ADAPTER_TEMPLATES: AdapterTemplate[] = [
  // -------- Claude Pro 订阅 (CLI) --------
  {
    id: "claude-pro",
    label: "Claude (Pro 订阅)",
    description: "本机 claude CLI · OAuth 登录走你的 Pro 订阅 · 不需要 API key",
    defaultTrustScope: "confirm-once",
    preInstall: [
      "npm i -g @anthropic-ai/claude-code",
      "claude   # 跟着浏览器 OAuth 登录一次",
    ],
    fields: [
      {
        key: "id",
        label: "Adapter ID (Web 端 @ 后面用的名字)",
        type: "text",
        required: true,
        default: "claude",
        placeholder: "claude",
      },
    ],
    toToml: (v) => `
[[adapter]]
id           = "${v.id || "claude"}"
kind         = "cli"
command      = "claude"
args         = ["-p"]
trust_scope  = "confirm-once"
description  = "Claude Code (Pro 订阅) · 写代码 / 跑命令"

[[adapter.usages]]
label       = "-p <prompt>"
template    = "-p "
description = "one-shot prompt"

[[adapter.usages]]
label       = "interactive <prompt>"
template    = "interactive "
description = "打开可交互终端会话"

[[adapter.usages]]
label       = "--continue"
template    = "--continue "
description = "继续上一次会话"
`.trim(),
  },

  // -------- Codex Pro 订阅 (CLI) --------
  {
    id: "codex-pro",
    label: "Codex (ChatGPT Plus 订阅)",
    description: "本机 codex CLI · ChatGPT Plus 订阅 · 不需要 API key",
    defaultTrustScope: "confirm-once",
    preInstall: [
      "npm i -g @openai/codex",
      "codex   # 一次性 OAuth",
    ],
    fields: [
      {
        key: "id",
        label: "Adapter ID",
        type: "text",
        required: true,
        default: "codex",
      },
    ],
    toToml: (v) => `
[[adapter]]
id           = "${v.id || "codex"}"
kind         = "cli"
command      = "codex"
args         = []
trust_scope  = "confirm-once"
description  = "Codex (ChatGPT Plus 订阅) · 代码生成 / 编辑"

[[adapter.usages]]
label       = "exec <prompt>"
template    = "exec "
description = "一次性执行"

[[adapter.usages]]
label       = "interactive <prompt>"
template    = "interactive "
description = "打开可交互终端会话"
`.trim(),
  },

  // -------- OpenAI API (HTTP) --------
  {
    id: "openai-api",
    label: "OpenAI API (按 token 计费)",
    description: "需要 sk- 开头的 API key, 流式 token-by-token 输出",
    defaultTrustScope: "confirm-once",
    fields: [
      { key: "id", label: "Adapter ID", type: "text", required: true, default: "gpt" },
      {
        key: "model",
        label: "Model",
        type: "select",
        required: true,
        default: "gpt-4o",
        options: ["gpt-4o", "gpt-4o-mini", "gpt-4-turbo", "o1-mini"],
      },
      {
        key: "api_key",
        label: "OpenAI API Key",
        type: "password",
        required: true,
        placeholder: "sk-...",
        secret: true,
        help: "platform.openai.com/api-keys",
      },
    ],
    toToml: (v) => `
[[adapter]]
id              = "${v.id || "gpt"}"
kind            = "http"
url             = "https://api.openai.com/v1/chat/completions"
trust_scope     = "confirm-once"
description     = "OpenAI ${v.model || "gpt-4o"} (API) · 流式"
timeout_seconds = 120
stream          = true
stream_format   = "openai-sse"
stream_path     = "choices.0.delta.content"

  [adapter.headers]
  "Authorization" = "Bearer ${v.api_key || "sk-REPLACE-ME"}"

  body_template = '''
{"model":"${v.model || "gpt-4o"}","stream":true,"messages":[{"role":"user","content":{prompt}}]}
'''
  response_path = "choices.0.message.content"
`.trim(),
  },

  // -------- Anthropic API (HTTP) --------
  {
    id: "anthropic-api",
    label: "Anthropic API (按 token 计费)",
    description: "需要 sk-ant- 开头的 API key, 流式",
    defaultTrustScope: "confirm-once",
    fields: [
      { key: "id", label: "Adapter ID", type: "text", required: true, default: "claude-api" },
      {
        key: "model",
        label: "Model",
        type: "select",
        required: true,
        default: "claude-3-5-sonnet-latest",
        options: ["claude-3-5-sonnet-latest", "claude-3-5-haiku-latest", "claude-3-opus-latest"],
      },
      {
        key: "api_key",
        label: "Anthropic API Key",
        type: "password",
        required: true,
        placeholder: "sk-ant-...",
        secret: true,
        help: "console.anthropic.com",
      },
    ],
    toToml: (v) => `
[[adapter]]
id              = "${v.id || "claude-api"}"
kind            = "http"
url             = "https://api.anthropic.com/v1/messages"
trust_scope     = "confirm-once"
description     = "Anthropic ${v.model || "claude-3-5-sonnet-latest"} (API) · 流式"
timeout_seconds = 120
stream          = true
stream_format   = "anthropic-sse"
stream_path     = "delta.text"

  [adapter.headers]
  "x-api-key"          = "${v.api_key || "sk-ant-REPLACE-ME"}"
  "anthropic-version"  = "2023-06-01"

  body_template = '''
{"model":"${v.model || "claude-3-5-sonnet-latest"}","max_tokens":2048,"stream":true,"messages":[{"role":"user","content":{prompt}}]}
'''
  response_path = "content.0.text"
`.trim(),
  },

  // -------- Ollama 本地 --------
  {
    id: "ollama",
    label: "本地 Ollama (完全离线)",
    description: "本机跑 ollama serve · 无需 API key · 隐私 + 免费",
    defaultTrustScope: "auto",
    preInstall: [
      "brew install ollama   # 或 https://ollama.com 下载",
      "ollama serve",
      "ollama pull llama3   # 或其他模型",
    ],
    fields: [
      { key: "id", label: "Adapter ID", type: "text", required: true, default: "llama3" },
      {
        key: "model",
        label: "Model (须先 ollama pull)",
        type: "text",
        required: true,
        default: "llama3",
        placeholder: "llama3 / qwen2.5 / mistral / ...",
      },
      {
        key: "url",
        label: "Ollama URL",
        type: "text",
        required: true,
        default: "http://localhost:11434/api/generate",
      },
    ],
    toToml: (v) => `
[[adapter]]
id              = "${v.id || "llama3"}"
kind            = "http"
url             = "${v.url || "http://localhost:11434/api/generate"}"
trust_scope     = "auto"
description     = "本地 Ollama ${v.model || "llama3"} · 完全离线"
timeout_seconds = 300
stream          = true
stream_format   = "ndjson"
stream_path     = "response"

  body_template = '''
{"model":"${v.model || "llama3"}","prompt":{prompt},"stream":true}
'''
  response_path = "response"
`.trim(),
  },
];

export function listAdapterTemplates(): AdapterTemplate[] {
  return ADAPTER_TEMPLATES;
}

export function getAdapterTemplate(id: string): AdapterTemplate | undefined {
  return ADAPTER_TEMPLATES.find((t) => t.id === id);
}

/** E9.9b: 单条访客配额规则 (UI 编辑用). */
export interface QuotaRow {
  user_id: string;
  daily_token_limit?: number;
  daily_usd_limit?: number;
  monthly_token_limit?: number;
  total_usd_limit?: number;
}

/** 把 quota rows 序列化成 [[adapter.quotas]] toml 段, 空数组返空字符串. */
export function quotasToToml(quotas: QuotaRow[] | undefined): string {
  if (!quotas || quotas.length === 0) return "";
  return quotas
    .filter((q) => q.user_id.trim() !== "")
    .map((q) => {
      const lines = [`  [[adapter.quotas]]`, `  user_id = "${q.user_id.trim()}"`];
      if (q.daily_token_limit != null && q.daily_token_limit > 0) {
        lines.push(`  daily_token_limit   = ${Math.round(q.daily_token_limit)}`);
      }
      if (q.daily_usd_limit != null && q.daily_usd_limit > 0) {
        lines.push(`  daily_usd_limit     = ${q.daily_usd_limit}`);
      }
      if (q.monthly_token_limit != null && q.monthly_token_limit > 0) {
        lines.push(`  monthly_token_limit = ${Math.round(q.monthly_token_limit)}`);
      }
      if (q.total_usd_limit != null && q.total_usd_limit > 0) {
        lines.push(`  total_usd_limit     = ${q.total_usd_limit}`);
      }
      return lines.join("\n");
    })
    .join("\n\n");
}

/** 把多个 adapter toml snippet 合并成一份完整 config 头 + adapters */
export function buildFullConfig(
  adapterTomlSnippets: string[],
  options?: { device_id?: string; rooms?: string[] },
): string {
  const rooms = options?.rooms ?? ["ai-pair"];
  const roomsToml = rooms.map((r) => `"${r}"`).join(", ");
  const header = `# ChatBox Agent Host 配置 (由 Web UI 生成)
# 保存到 ~/.chatbox/agent-host.toml 后跑:
#   cargo run -p chatbox-agent-host -- start --config ~/.chatbox/agent-host.toml
# 或下载发布版二进制再执行

relay_url   = "wss://chatbox.jennieapp.com/agent/connect"
auth_token  = "demo"
device_id   = "${options?.device_id ?? "my-mac"}"
join_rooms  = [${roomsToml}]

[gate]
rate_per_min  = 10
allowed_paths = ["~/chatbox-workspace", "~/code"]
audit_db      = "~/.chatbox/agent-audit.db"
`;
  return header + "\n" + adapterTomlSnippets.join("\n\n") + "\n";
}
